import { Typography } from '@veraclins-dev/ui';
import { cn } from '@veraclins-dev/utils';
import type { ReactNode } from 'react';

import {
  AppExternalLink,
  type AppExternalLinkProps,
  AppLink,
  type AppLinkProps,
} from '../../link';

const ctaClassName =
  'inline-flex min-h-11 items-center gap-2.5 rounded-full border px-5 py-3.5 font-mono text-xs font-normal uppercase leading-normal tracking-widest transition hover:-translate-y-px';

const ctaEmphasisClassName = (emphasis: unknown) =>
  emphasis === 'outline'
    ? 'border-border bg-transparent text-foreground hover:border-foreground'
    : 'border-transparent bg-foreground text-card hover:bg-foreground/90';

interface EditorialCtaContentProps {
  children: ReactNode;
  showArrow?: boolean;
  arrow?: string;
}

function EditorialCtaContent({
  children,
  showArrow = true,
  arrow = '→',
}: EditorialCtaContentProps) {
  return (
    <>
      <Typography component="span" variant="inherit">
        {children}
      </Typography>
      {showArrow ? (
        <Typography
          component="span"
          variant="inherit"
          aria-hidden="true"
          className="text-sm leading-none"
        >
          {arrow}
        </Typography>
      ) : null}
    </>
  );
}

export function EditorialLink({
  children,
  className,
  emphasis = 'filled',
  tone = 'brand',
  showArrow,
  arrow,
  ...props
}: AppLinkProps & { showArrow?: boolean; arrow?: string }) {
  return (
    <AppLink
      type="button"
      emphasis={emphasis}
      tone={tone}
      linkSize="lg"
      underline="none"
      {...props}
      className={cn(ctaClassName, ctaEmphasisClassName(emphasis), className)}
    >
      <EditorialCtaContent showArrow={showArrow} arrow={arrow}>
        {children}
      </EditorialCtaContent>
    </AppLink>
  );
}

export function EditorialExternalLink({
  children,
  className,
  emphasis = 'filled',
  tone = 'brand',
  showArrow,
  arrow,
  ...props
}: AppExternalLinkProps & { showArrow?: boolean; arrow?: string }) {
  return (
    <AppExternalLink
      type="button"
      emphasis={emphasis}
      tone={tone}
      linkSize="lg"
      underline="none"
      {...props}
      className={cn(ctaClassName, ctaEmphasisClassName(emphasis), className)}
    >
      <EditorialCtaContent showArrow={showArrow} arrow={arrow}>
        {children}
      </EditorialCtaContent>
    </AppExternalLink>
  );
}
